/**
 * AffDice Affiliate — entry point.
 *
 * §3.7's session rides an `HttpOnly` cookie the browser manages — never
 * `localStorage`, still, since anything script-readable is readable by an
 * injected script too. The cookie is what makes a reload not sign somebody
 * out; `useExistingSession` below is the boot-time check for one, using a
 * client built with no token, since the whole point is asking whether the
 * cookie alone is already enough.
 *
 * Impersonation (below) is the one flow that stays Bearer-only: a support
 * session it mints is a `sessions` row like any other, but nothing here can
 * make the *browser* attach it as a cookie — only a `Set-Cookie` response
 * can, and this token arrives already-minted, in a URL, from `affdice-admin`
 * — not from a `/v1/login` round trip this page makes itself. Its own 30-
 * minute cap already keeps it short-lived, so it losing the "survives a
 * reload" property this file otherwise adds is the acceptable edge, not a
 * regression worth a second cookie-issuing endpoint to close.
 */

import { useEffect, StrictMode, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Callout,
  ChangePassword,
  EnrolMfa,
  Loading,
  LoginAsBanner,
  NotConfigured,
  SignIn,
  ToastProvider,
  explainFailure,
  onboardingApi,
  postLogin,
  preferredShippedLocale,
  publicRoute,
  setDisplayLocale,
  useExistingSession,
  useLoad,
  useLoginAsHandoff,
  useSessionExpired,
} from '@affdice/portal-kit';

import '@affdice/portal-kit/styles.css';

import { App } from './App.tsx';
import { ClosedProgrammePage } from './ClosedProgramme.tsx';
import { AffiliateApi } from './client.ts';
import {
  NO_ACTIVE_PROGRAMME_EVENT,
  currentRequestSeq,
  isStaleClosedEvent,
  type ClosedProgramme,
  type ClosedProgrammeEvent,
} from './closed-programme.ts';
import { InvitationPage } from './Invitation.tsx';
import { SignUpPage } from './SignUp.tsx';
import { VerifyPage } from './Verify.tsx';

const baseUrl = import.meta.env['VITE_API_BASE'];
/*
 * §2.8d — what to read the sign-in form in, before there is a session to ask.
 *
 * Once somebody is signed in, `App` sets this from their saved choice and
 * their operator's default, and that supersedes whatever is decided here. This
 * is only for the screens that come first: a person who cannot read English
 * meets one of those before anything else, and a form they cannot read is not
 * a form they can sign in through to reach the picker.
 */
setDisplayLocale(preferredShippedLocale(navigator.languages));


function Root() {
  if (!baseUrl) return <NotConfigured portal="the KD Affiliate" />;
  return <RootBooted baseUrl={baseUrl} />;
}

function RootBooted({ baseUrl }: { baseUrl: string }) {
  const bootApi = useMemo(
    () => new AffiliateApi({ baseUrl, token: () => undefined }),
    [baseUrl],
  );

  // §3.6 — support opens this portal with a one-time impersonation token in
  // the URL rather than a password. Read once, then stripped from the URL —
  // see this file's own top comment for why this one flow stays Bearer-only.
  const [urlSession] = useState<{ token: string; permissions: readonly string[] } | null>(() => {
    const fromUrl = new URLSearchParams(location.search).get('impersonate_token');
    if (!fromUrl) return null;
    history.replaceState(null, '', location.pathname + location.hash);
    return { token: fromUrl, permissions: [] };
  });

  // §3.13 — AffDice SuperAdmin opens this portal with a one-time code for a session it
  // minted as this affiliate; see portal-kit's login-as.tsx.
  const handoff = useLoginAsHandoff(baseUrl);

  const existing = useExistingSession(bootApi);

  const [session, setSession] = useState<{
    token?: string;
    permissions: readonly string[];
  } | null>(urlSession);
  const [booted, setBooted] = useState(urlSession !== null);
  // 0332 — a session with no programme left to open, met at sign-in or
  // mid-use, swaps the console for the page that says so. Choosing another
  // programme opens a fresh console on it (`consoleKey`).
  const [closed, setClosed] = useState<ClosedProgramme | null>(null);
  const [consoleKey, setConsoleKey] = useState(0);
  // The request number at the last switch; see isStaleClosedEvent.
  const switchedAt = useRef(0);
  useEffect(() => {
    const listener = (e: Event) => {
      const detail = (e as CustomEvent<ClosedProgrammeEvent>).detail;
      // A request the old console started before the switch, answering late.
      if (isStaleClosedEvent(detail, switchedAt.current)) return;
      setClosed({ closedOperator: detail.closedOperator });
    };
    window.addEventListener(NO_ACTIVE_PROGRAMME_EVENT, listener);
    return () => window.removeEventListener(NO_ACTIVE_PROGRAMME_EVENT, listener);
  }, []);

  // A session that ends mid-use goes straight back to the sign-in form, rather
  // than leaving the screen it ended on to say so.
  useSessionExpired(() => {
    setClosed(null);
    setSession(null);
  });

  useEffect(() => {
    if (handoff.state === 'ready') {
      setSession({ token: handoff.token, permissions: [] });
      setBooted(true);
      return;
    }
    if (handoff.state === 'redeeming') return; // never the cookie's user instead.
    if (session) return; // the impersonation URL token already answered this.
    if (existing.state === 'checking') return;
    if (existing.state === 'found') setSession({ permissions: [] });
    setBooted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existing.state, handoff.state]);

  // §3.7 — the two steps between being given an account and having one. Both
  // still need the credentials, and there is no session yet to carry them.
  const [onboarding, setOnboarding] = useState<
    { step: 'enrol' | 'password'; email: string; password: string } | null
  >(null);
  const onboard = onboardingApi(baseUrl);

  if (onboarding?.step === 'enrol') {
    return (
      <EnrolMfa
        email={onboarding.email}
        password={onboarding.password}
        api={onboard}
        onDone={() => setOnboarding(null)}
      />
    );
  }

  if (onboarding?.step === 'password') {
    return (
      <ChangePassword
        email={onboarding.email}
        currentPassword={onboarding.password}
        api={onboard}
        onDone={() => setOnboarding(null)}
      />
    );
  }

  // §6.14c, §6.15 — both of these come before any session exists, so they are
  // checked before the sign-in gate rather than behind it. A link from an email
  // that landed on a login form would be a dead end.
  //
  // Paths rather than query flags, and the same paths the outbound mail links
  // to — see `publicRoute`.
  const route = publicRoute(location);
  const anon = () => new AffiliateApi({ baseUrl, token: () => undefined });

  if (route.page === 'invite') {
    return <InvitationPage api={anon()} token={route.token ?? ''} />;
  }

  if (route.page === 'verify') {
    return <VerifyPage api={anon()} token={route.token ?? ''} />;
  }

  // §6.15 — the public application page. The programme is named in the path
  // (`/signup/northline`) because an operator prints that link; there is no
  // session and nothing else to say which programme this is.
  if (route.page === 'apply') {
    return <SignUpPage api={anon()} code={route.tail ?? ''} />;
  }

  if (!booted) return <Loading />;

  if (handoff.state === 'failed' && !session) {
    return <div className="main"><Callout tone="caution">{handoff.error}</Callout></div>;
  }

  /*
   * An unreachable API used to end here, on a message with nothing to press.
   * A reader who reloaded into an outage, or into a session that had gone,
   * got a dead end instead of the sign-in form they came for. Falling through
   * puts the form in front of them; if the service really is down, the attempt
   * says so, and if the session had merely expired they are already where they
   * needed to be.
   */
  if (!session) {
    return (
      <SignIn
        title="KD Affiliate"
        post={(body) => postLogin(baseUrl, { ...body, portal: 'affiliate' })}
        onSignedIn={(token, permissions) => setSession({ token, permissions })}
        onNeedsEnrolment={(email, password) => setOnboarding({ step: 'enrol', email, password })}
        onMustChangePassword={(email, password) =>
          setOnboarding({ step: 'password', email, password })
        }
      />
    );
  }

  const signedInApi = new AffiliateApi({ baseUrl, token: () => session.token });
  const signOut = () => {
    setClosed(null);
    setSession(null);
  };

  return (
    <>
      <LoginAsBanner baseUrl={baseUrl} token={session.token} />
      {closed ? (
        <ClosedProgrammePage
          api={signedInApi}
          closed={closed}
          onChosen={() => {
            switchedAt.current = currentRequestSeq();
            setClosed(null);
            setConsoleKey((k) => k + 1);
          }}
          onSignedOut={signOut}
        />
      ) : (
        <SignedIn key={consoleKey} api={signedInApi} session={session} onSignedOut={signOut} />
      )}
    </>
  );
}

/**
 * §3.6 — permissions are re-read on every load.
 *
 * Trusting the set handed back at sign-in means a role revoked mid-session
 * keeps working until the person happens to sign in again, and §3.6's
 * time-boxed elevation is exactly the case where that matters: the elevation
 * expires and the console does not notice.
 */
function SignedIn({
  api,
  session,
  onSignedOut,
}: {
  api: AffiliateApi;
  session: { token?: string; permissions: readonly string[] };
  onSignedOut: () => void;
}) {
  const fresh = useLoad(() => api.permissions(), []);
  const permissions =
    fresh.state === 'ready' ? fresh.value.permissions : session.permissions;

  return (
    <App
      api={api}
      now={() => new Date()}
      permissions={permissions}
      onboard={onboardingApi(baseUrl)}
      onSignedOut={onSignedOut}
    />
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ToastProvider>
      <Root />
    </ToastProvider>
  </StrictMode>,
);
